package navigation.home.task.alltasks

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.selection.SelectionContainer
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.*
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.input.key.Key
import androidx.compose.ui.input.key.KeyEventType
import androidx.compose.ui.input.key.key
import androidx.compose.ui.input.key.type
import androidx.compose.ui.unit.dp
import cafe.adriel.voyager.koin.koinScreenModel
import cafe.adriel.voyager.navigator.LocalNavigator
import cafe.adriel.voyager.navigator.currentOrThrow
import com.essntl.components.sidepanel.SidePanel
import com.essntl.components.sidepanel.SizeConstraint
import com.essntl.components.sidepanel.Splitter
import com.essntl.components.sidepanel.rememberSidePanelState
import com.essntl.features.client.domain.model.ClientModel
import com.essntl.features.client.ui.AddEditClientDialog
import com.essntl.features.client.ui.addeditclient.AddEditClientEvent
import com.essntl.features.itinerary.domain.model.ItineraryModel
import com.essntl.features.task.domain.model.TaskModel
import com.essntl.features.task.ui.addedittask.AddEditTaskEvent
import com.essntl.features.task.ui.addedittask.AddEditTaskNavigation
import com.essntl.features.task.ui.addedittask.AddEditTaskScreen
import com.essntl.features.task.ui.alltasks.AllTasksEvent
import com.essntl.features.task.ui.alltasks.AllTasksNavigation
import com.essntl.features.task.ui.alltasks.AllTasksScreen
import com.essntl.features.task.ui.alltasks.components.OverdueTasksDialog
import essntl.key.LocalKeyEventManager
import com.essntl.uikit.components.iconbutton.outline.EsMediumOutlineIconButton
import com.essntl.uikit.components.icons.EsIcons
import com.essntl.uikit.components.link.NavLinkState
import com.essntl.uikit.components.utils.EsConstants
import com.essntl.uikit.components.utils.spacing
import com.essntl.uikit.theme.esColorScheme
import com.essntl.uikit.theme.esTypography
import kotlinx.coroutines.flow.collectLatest
import navigation.home.clients.addeditclient.AddEditClientScreenModel
import navigation.home.edititinerary.EditItineraryScreenNav
import navigation.home.task.addedittask.AddEditTaskScreenModel
import navigation.utils.NavLinkScreen

object AllTasksScreenNav : NavLinkScreen {
    override val defaultTitle: String = "Tasks"

    override var navLinkState: NavLinkState by mutableStateOf(
        NavLinkState(
            title = defaultTitle,
            iconRes = EsIcons.Task,
        ),
    )

    @Composable
    override fun Content() {
        val allTaskScreenModel = koinScreenModel<AllTasksScreenModel>()
        val allTasksState by allTaskScreenModel.state.collectAsState()

        val addEditTaskScreenModel = koinScreenModel<AddEditTaskScreenModel>()
        val addEditTaskState by addEditTaskScreenModel.state.collectAsState()

        val addEditClientScreenModel = koinScreenModel<AddEditClientScreenModel>()
        val addEditClientState by addEditClientScreenModel.state.collectAsState()

        var openAddEditClientDialog by rememberSaveable { mutableStateOf(false) }
        var clientToEdit by remember { mutableStateOf<ClientModel?>(null) }
        var itineraryToEdit by remember { mutableStateOf<ItineraryModel?>(null) }

        var isAllOverdueTaskDialogExpanded by rememberSaveable { mutableStateOf(false) }

        val navigator = LocalNavigator.currentOrThrow

        val sidePanelState =
            rememberSidePanelState(
                sidePanelVisible = false,
                panelSizeConstraint =
                    SizeConstraint(
                        minSize = 300.dp,
                        maxSize = 600.dp,
                    ),
            )

        LaunchedEffect(Unit) {
            navLinkState = navLinkState.copy(
                onRefresh = {
                    allTaskScreenModel.onEvent(AllTasksEvent.LoadTasks(refresh = true))
                    sidePanelState.sidePanelOpened = false
                },
            )
        }

        val keyEventManager = LocalKeyEventManager.current

        LaunchedEffect(keyEventManager) {
            keyEventManager.events.collectLatest {
                if (it.key == Key.H && it.type == KeyEventType.KeyUp) {
                    sidePanelState.sidePanelOpened = false
                }
            }
        }

        LaunchedEffect(sidePanelState.sidePanelOpened) {
            if (!sidePanelState.sidePanelOpened) {
                addEditTaskScreenModel.onEvent(AddEditTaskEvent.ClearTaskState)
            }
        }

        LaunchedEffect(addEditTaskState.addEditTask) {
            if (addEditTaskState.addEditTask.isSuccess) {
                addEditTaskState.addEditTask.data?.let { task ->
                    allTaskScreenModel.onEvent(
                        AllTasksEvent.AddEditTask(
                            task = task,
                        ),
                    )
                }
                addEditTaskScreenModel.onEvent(AddEditTaskEvent.ClearSuccessState)
            }
        }

        LaunchedEffect(addEditClientState.addEditClient.isSuccess) {
            if (addEditClientState.addEditClient.isSuccess) {
                addEditClientState.addEditClient.data?.let {
                    itineraryToEdit?.let { i ->
                        clientToEdit?.let { c ->
                            allTaskScreenModel.onEvent(
                                AllTasksEvent.EditClient(
                                    it,
                                    i,
                                ),
                            )
                        }
                    }
                }
                addEditClientScreenModel.onEvent(AddEditClientEvent.ClearClientState)
                openAddEditClientDialog = false
                clientToEdit = null
                itineraryToEdit = null
            }
        }

        SidePanel(
            sidePanelState = sidePanelState,
            isHorizontal = true,
            sidePanel = {
                Column(
                    modifier =
                        Modifier
                            .fillMaxHeight()
                            .padding(end = 1.5f.spacing)
                            .clip(RoundedCornerShape(EsConstants.SectionBorderRadius)),
                ) {
                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.SpaceBetween,
                        modifier =
                            Modifier
                                .background(MaterialTheme.esColorScheme.bgSecondary)
                                .padding(2.5f.spacing)
                                .fillMaxWidth(),
                    ) {
                        val isEdit by remember(addEditTaskState.task.value.id) {
                            mutableStateOf(
                                addEditTaskState.task.value.id.isNotBlank(),
                            )
                        }

                        EsMediumOutlineIconButton(
                            onClick = {
                                sidePanelState.sidePanelOpened = false
                            },
                            iconRes = EsIcons.ArrowRight,
                        )

                        if (isEdit) {
                            SelectionContainer {
                                Text(
                                    text = addEditTaskState.task.value.title ?: "",
                                    color = MaterialTheme.esColorScheme.textPrimary,
                                    style = MaterialTheme.esTypography.h3Bold24,
                                )
                            }
                        } else {
                            Text(
                                text = "New task",
                                color = MaterialTheme.esColorScheme.textPrimary,
                                style = MaterialTheme.esTypography.h3Bold24,
                            )
                        }
                    }

                    AddEditTaskScreen(
                        state = addEditTaskState,
                        onEvent = addEditTaskScreenModel::onEvent,
                        validation = addEditTaskScreenModel.validation,
                        getAllTask = allTasksState.getAllTasks.data,
                        onUpdateTaskStatus = { taskId, status ->
                            allTaskScreenModel.onEvent(
                                AllTasksEvent.UpdateTaskStatus(
                                    taskId = taskId,
                                    status = status,
                                ),
                            )
                        },
                        onNavigation = {
                            when (it) {
                                is AddEditTaskNavigation.EditItinerary -> {
                                    navigator.push(
                                        EditItineraryScreenNav(
                                            itineraryId = it.itineraryId,
                                        ),
                                    )
                                }
                            }
                        },
                    )
                }
            },
            splitter =
                Splitter(
                    measuredPart = {
                        Box(
                            modifier = Modifier.width(1.spacing).fillMaxHeight(),
                        )
                    },
                ),
            modifier = Modifier.fillMaxSize(),
        ) {
            AllTasksScreen(
                state = allTasksState,
                addEditTaskState = addEditTaskState,
                onEvent = {
                    allTaskScreenModel.onEvent(it)
                },
                onNavigation = {
                    when (it) {
                        is AllTasksNavigation.CloseSidePanel -> {
                            addEditTaskScreenModel.onEvent(AddEditTaskEvent.ClearSuccessState)
                            sidePanelState.sidePanelOpened = false
                        }

                        is AllTasksNavigation.AddTask -> {
                            addEditTaskScreenModel.onEvent(AddEditTaskEvent.ClearTaskState)
                            sidePanelState.sidePanelOpened = true
                        }

                        is AllTasksNavigation.EditTask -> {
                            if (
                                sidePanelState.sidePanelOpened &&
                                addEditTaskState.getTask.data?.id == it.taskId
                            ) {
                                return@AllTasksScreen
                            }

                            addEditTaskScreenModel.onEvent(AddEditTaskEvent.ClearTaskState)
                            addEditTaskScreenModel.onEvent(AddEditTaskEvent.GetTask(it.taskId))
                            sidePanelState.sidePanelOpened = true
                        }

                        is AllTasksNavigation.EditClient -> {
                            clientToEdit = it.client
                            itineraryToEdit = it.itinerary
                            openAddEditClientDialog = true
                        }

                        is AllTasksNavigation.ShowOverdueTasks -> {
                            isAllOverdueTaskDialogExpanded = true
                        }
                    }
                },
            )

            AddEditClientDialog(
                openDialog = openAddEditClientDialog,
                currentClient = clientToEdit,
                state = addEditClientState,
                onEvent = addEditClientScreenModel::onEvent,
                validation = addEditClientScreenModel.validation,
                onDismissRequest = {
                    openAddEditClientDialog = false
                    clientToEdit = null
                    itineraryToEdit = null
                },
            )

            if (isAllOverdueTaskDialogExpanded)
                OverdueTasksDialog(
                    state = allTasksState,
                    onEvent = allTaskScreenModel::onEvent,
                    onNavigation = {},
                    selectedTask = TaskModel.empty,
                    isLoading = allTasksState.isLoading,
                    onDismissRequest = {
                        isAllOverdueTaskDialogExpanded = false
                    },
                )
        }
    }
}
